import { useMemo, useState } from 'preact/hooks';
import { animals, schedules, nowTick } from '@/state/data';
import { computeNextDue, dueState, timeShort, type DueState } from '@/lib/due';
import { initials, avatarClass, type Animal, type AnimalCategory, type Schedule } from '@/types';
import { Icon } from '@/components/Icon';
import { VirtualList } from '@/components/VirtualList';
import { navigate, back } from '@/state/nav';

type Filter = 'all' | AnimalCategory | 'released';

const FILTER_LABEL: Record<string, string> = {
  all: 'All', bird: 'Birds', raptor: 'Raptors', mammal: 'Mammals',
  reptile: 'Reptiles', rabbit: 'Rabbits', other: 'Other', released: 'Released',
};

function scheduleSummary(s: Schedule | undefined): string {
  if (!s) return 'No schedule';
  if (s.stype === 'interval') {
    const m = s.interval_minutes ?? 0;
    return m % 60 === 0 ? `every ${m / 60}hr` : `every ${m}min`;
  }
  const times = (s.times ?? []).map((t) => {
    const [h, mi] = t.split(':').map(Number);
    const ap = h >= 12 ? 'p' : 'a';
    const hh = h % 12 === 0 ? 12 : h % 12;
    return mi ? `${hh}:${String(mi).padStart(2, '0')}${ap}` : `${hh}${ap}`;
  });
  const base = times.join(' · ');
  return s.feed_every_n_days ? `every ${s.feed_every_n_days} days · ${base}` : base;
}

function statusChip(state: DueState, animal: Animal) {
  if (animal.condition === 'critical') return <span class="pill pill--warn">Critical</span>;
  if (state === 'overdue') return <span class="pill pill--danger">Late</span>;
  if (state === 'due_soon') return <span class="pill pill--info">Due</span>;
  return null;
}

export function RosterScreen() {
  void nowTick.value;
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const all = useMemo(() => [...animals.value.values()], [animals.value]);
  const schedByAnimal = useMemo(() => {
    const m = new Map<string, Schedule>();
    for (const s of schedules.value.values()) {
      if (s.enabled && !m.has(s.animal_id)) m.set(s.animal_id, s);
    }
    return m;
  }, [schedules.value]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: 0, released: 0 };
    for (const a of all) {
      if (a.status === 'active') {
        c.all++;
        c[a.category] = (c[a.category] ?? 0) + 1;
      } else if (a.status === 'released') c.released++;
    }
    return c;
  }, [all]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all
      .filter((a) => {
        if (filter === 'released') return a.status === 'released';
        if (a.status !== 'active') return false;
        if (filter !== 'all' && a.category !== filter) return false;
        if (!q) return true;
        return (
          a.name.toLowerCase().includes(q) ||
          (a.species ?? '').toLowerCase().includes(q) ||
          (a.diet ?? '').toLowerCase().includes(q)
        );
      })
      .sort((x, y) => x.name.localeCompare(y.name));
  }, [all, query, filter]);

  const filters: Filter[] = ['all', 'bird', 'raptor', 'mammal', 'rabbit', 'reptile', 'other', 'released'];

  return (
    <div class="screen screen--fade-in">
      <header class="header header--sub">
        <button class="iconbtn" aria-label="Back" onClick={() => back('/more')}>
          <Icon name="chevron-left" size={20} />
        </button>
        <div>
          <div class="header__title">Roster</div>
        </div>
        <div class="header__spacer" />
        <button class="iconbtn" aria-label="Add animal" onClick={() => navigate('/roster/new')}>
          <Icon name="plus" size={22} />
        </button>
      </header>

      <div class="roster-search">
        <Icon name="search" size={16} />
        <input
          class="input"
          type="search"
          placeholder="Search animals or batches"
          aria-label="Search animals"
          value={query}
          onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
        />
      </div>

      <div class="roster-chips" role="tablist">
        {filters.map((f) =>
          f === 'all' || (counts[f] ?? 0) > 0 ? (
            <button
              key={f}
              class={`pill ${filter === f ? 'pill--accent' : ''}`}
              role="tab"
              aria-selected={filter === f}
              onClick={() => setFilter(f)}
            >
              {FILTER_LABEL[f]} · {counts[f] ?? 0}
            </button>
          ) : null
        )}
      </div>

      {list.length === 0 ? (
        <p class="roster-empty">
          {query ? `Nothing matches "${query}".` : 'No animals here yet.'}
        </p>
      ) : (
        <VirtualList
          class="roster-list"
          items={list}
          rowHeight={76}
          render={(a) => {
            const sched = schedByAnimal.get(a.id);
            const nextDue = sched ? computeNextDue(sched) : null;
            const state = dueState(nextDue);
            return (
              <button class="roster-card" onClick={() => navigate(`/roster/${a.id}`)}>
                <span class={`avatar ${avatarClass(a.category)}`}>{initials(a.name)}</span>
                <div class="roster-card__main">
                  <div class="roster-card__name">
                    <span>
                      {a.name}
                      {a.species && <span class="species"> · {a.species.toLowerCase()}</span>}
                    </span>
                    {a.kind === 'batch' && <span class="feed-card__count">×{a.count}</span>}
                  </div>
                  <p class="roster-card__meta">
                    {a.status === 'released'
                      ? `Released ${a.released_date ?? ''}`
                      : [
                          a.life_stage,
                          scheduleSummary(sched),
                          nextDue ? `next ${timeShort(nextDue)}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                  </p>
                </div>
                {a.status === 'active' && statusChip(state, a)}
              </button>
            );
          }}
        />
      )}
    </div>
  );
}
